/**
 * Small player conveniences:
 *   - Sounds: a soft chime when it's your turn, a more urgent one for "Are you still there?"
 *   - Large print: bigger cards and text
 *   - Send feedback: a note to the developer, saved on the server with what was on the table
 * Sound and large print are personal settings saved on this device.
 */

import { h } from '../dom.js';
import { app, rerender, send } from '../state.js';

// ---------------------------------------------------------------------------
// Saved settings
// ---------------------------------------------------------------------------

function load(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : v === 'on';
  } catch {
    return fallback;
  }
}

function save(key: string, on: boolean): void {
  try {
    localStorage.setItem(key, on ? 'on' : 'off');
  } catch {
    /* private mode: lasts for this tab */
  }
}

export const settings = {
  sound: load('euchre.sound', true),
  largePrint: load('euchre.largePrint', false),
};

export function setSound(on: boolean): void {
  settings.sound = on;
  save('euchre.sound', on);
  if (on) chime('turn'); // a sample, so you know what it sounds like
}

export function setLargePrint(on: boolean): void {
  settings.largePrint = on;
  save('euchre.largePrint', on);
  applyLargePrint();
}

export function applyLargePrint(): void {
  document.documentElement.dataset.print = settings.largePrint ? 'large' : 'normal';
}

// ---------------------------------------------------------------------------
// Sounds (made in the browser, no sound files to download)
// ---------------------------------------------------------------------------

let ctx: AudioContext | null = null;

/** Browsers only allow sound after the player has clicked or tapped something, so unlock it on the first one. */
function unlock(): void {
  try {
    ctx ??= new AudioContext();
    if (ctx.state === 'suspended') void ctx.resume();
  } catch {
    ctx = null;
  }
}
for (const ev of ['pointerdown', 'keydown'] as const) document.addEventListener(ev, unlock, { capture: true, passive: true });

function tone(freq: number, start: number, length: number, volume: number): void {
  if (!ctx) return;
  const t = ctx.currentTime + start;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(volume, t + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + length);
  osc.connect(gain).connect(ctx.destination);
  osc.start(t);
  osc.stop(t + length + 0.05);
}

/** turn: a soft two-note "ding-dong". stillThere: three brighter notes, a little louder. */
export function chime(kind: 'turn' | 'stillThere'): void {
  if (!settings.sound || !ctx || ctx.state !== 'running') return;
  if (kind === 'turn') {
    tone(784, 0, 0.5, 0.12); // G5
    tone(1047, 0.16, 0.7, 0.1); // C6
  } else {
    tone(988, 0, 0.35, 0.2); // B5
    tone(988, 0.3, 0.35, 0.2);
    tone(1319, 0.6, 0.6, 0.18); // E6
  }
}

// ---------------------------------------------------------------------------
// Menu controls (used in the table Menu)
// ---------------------------------------------------------------------------

/** A labelled pair of buttons, like "Sounds: On / Off". */
export function menuChoice<T extends string>(label: string, options: readonly [T, string][], current: T, pick: (v: T) => void): HTMLElement {
  return h(
    'div',
    { class: 'menu-talk', attrs: { role: 'group', 'aria-label': label } },
    h('span', { class: 'menu-talk-label', text: label }),
    options.map(([v, text]) =>
      h('button', {
        class: `btn small talk-btn${current === v ? ' on' : ''}`,
        type: 'button',
        text,
        attrs: { 'aria-pressed': String(current === v) },
        onclick: () => {
          pick(v);
          rerender();
        },
      }),
    ),
  );
}

// ---------------------------------------------------------------------------
// Send feedback
// ---------------------------------------------------------------------------

export const feedback = { open: false, draft: '', sending: false };

export function openFeedback(): void {
  feedback.open = true;
  app.ui.menuOpen = false;
  rerender();
  setTimeout(() => document.querySelector<HTMLTextAreaElement>('.feedback-card textarea')?.focus(), 0);
}

export function closeFeedback(): void {
  feedback.open = false;
  dialogEl = null;
  feedback.sending = false;
  rerender();
}

/** Called when the server confirms the note was saved. */
export function feedbackSaved(): void {
  feedback.open = false;
  dialogEl = null;
  feedback.sending = false;
  feedback.draft = '';
}

/** The server refused or failed: let them try again. */
export function feedbackFailed(): void {
  feedback.sending = false;
}

export function feedbackButton(cls = 'btn small'): HTMLElement {
  return h('button', { class: cls, type: 'button', text: 'Send feedback', onclick: openFeedback });
}

let dialogEl: HTMLElement | null = null;

/**
 * The feedback box. Built once per opening and kept, so the game redrawing (which happens often) never wipes
 * what you're typing or moves the cursor. main.ts mounts it outside the redrawn screen.
 */
export function feedbackDialog(): HTMLElement | null {
  if (!feedback.open) return null;
  if (dialogEl) {
    const btn = dialogEl.querySelector<HTMLButtonElement>('.feedback-send');
    if (btn) {
      btn.textContent = feedback.sending ? 'Sending…' : 'Send';
      btn.disabled = feedback.sending || !feedback.draft.trim();
    }
    return dialogEl;
  }
  const submit = () => {
    const text = feedback.draft.trim();
    if (!text || feedback.sending) return;
    feedback.sending = true;
    send({
      type: 'feedback',
      text,
      context: {
        screen: `${window.innerWidth}x${window.innerHeight}`,
        touch: window.matchMedia?.('(pointer: coarse)').matches ?? false,
        dealerTalk: app.ui.dealerTalk,
        largePrint: settings.largePrint,
        sound: settings.sound,
        browser: navigator.userAgent.slice(0, 120),
      },
    });
    rerender();
  };
  const area = h('textarea', {
    attrs: { rows: '5', maxlength: '1000', placeholder: 'What happened, or what would make the game better?', 'aria-label': 'Your feedback' },
  }) as HTMLTextAreaElement;
  area.value = feedback.draft;
  area.addEventListener('input', () => {
    feedback.draft = area.value;
    const btn = document.querySelector<HTMLButtonElement>('.feedback-send');
    if (btn) btn.disabled = !area.value.trim();
  });
  area.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter' && (ev.metaKey || ev.ctrlKey)) submit();
    if (ev.key === 'Escape') closeFeedback();
    ev.stopPropagation();
  });
  dialogEl = h(
    'div',
    {
      class: 'away-scrim feedback-scrim',
      onclick: (ev: Event) => {
        if (ev.target === ev.currentTarget) closeFeedback();
      },
    },
    h(
      'div',
      { class: 'away-card feedback-card', attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'feedback-title' } },
      h('h2', { id: 'feedback-title', text: 'Send feedback' }),
      h('p', { text: 'Tell us what happened or what could be better. We save your note with the table and the hand you’re on, so we can see exactly what you mean.' }),
      area,
      h(
        'div',
        { class: 'feedback-actions' },
        h('button', { class: 'btn', type: 'button', text: 'Cancel', onclick: closeFeedback }),
        h('button', {
          class: 'btn primary feedback-send',
          type: 'button',
          text: feedback.sending ? 'Sending…' : 'Send',
          disabled: feedback.sending || !feedback.draft.trim(),
          onclick: submit,
        }),
      ),
    ),
  );
  return dialogEl;
}
